/**
 * Manipulation Editor Component
 *
 * Modal editor for creating and editing SIP manipulation profiles.
 */

import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useFieldArray, useForm, useWatch, type FieldErrors, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronDown, ChevronUp, Info, Plus, Settings, Trash2, Wand2 } from 'lucide-react';
import type { Sip } from '@sbclite/core';
import { ResourceEditor, describeSubmitError, firstTabWithError, type ResourceEditorTab } from '../common';
import { useCreateSipManipulation, useUpdateSipManipulation } from '../../hooks/useSipManipulations';
import {
  MANIPULATION_ACTION_OPTIONS,
  MANIPULATION_FIELD_OPTIONS,
  MANIPULATION_TARGET_OPTIONS,
  MANIPULATION_TYPE_OPTIONS,
  buildManipulationDefaultValues,
  createEmptyManipulationRule,
  manipulationFormSchema,
  manipulationFormToCreatePayload,
  manipulationFormToUpdatePayload,
  reprioritizeManipulationRules,
  type ManipulationFormData,
} from './manipulationEditorModel';

interface ManipulationEditorProps {
  profile: Sip.ManipulationInfo | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function ManipulationEditor({ profile, onClose, onSuccess }: ManipulationEditorProps) {
  const isEditing = Boolean(profile);
  const [activeTab, setActiveTab] = useState('profile');
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [expandLastRule, setExpandLastRule] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ManipulationFormData>({
    resolver: zodResolver(manipulationFormSchema),
    defaultValues: buildManipulationDefaultValues(profile),
  });

  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: 'rules',
    keyName: 'formId',
  });

  useEffect(() => {
    form.reset(buildManipulationDefaultValues(profile));
    setActiveTab('profile');
    setExpandedRuleId(null);
    setExpandLastRule(false);
    setSubmitError(null);
  }, [profile, form]);

  useEffect(() => {
    if (!expandLastRule) return;
    const lastField = fields.at(-1);
    if (!lastField) return;
    setExpandedRuleId(lastField.formId);
    setActiveTab('rules');
    setExpandLastRule(false);
  }, [expandLastRule, fields]);

  const createMutation = useCreateSipManipulation();
  const updateMutation = useUpdateSipManipulation();
  const isSaving = createMutation.isPending || updateMutation.isPending;

  const addRule = useCallback(() => {
    const rules = form.getValues('rules');
    const maxPriority = rules.length > 0 ? Math.max(...rules.map((rule) => rule.priority)) : 0;
    append(createEmptyManipulationRule(maxPriority + 10));
    setExpandLastRule(true);
  }, [append, form]);

  const moveRule = useCallback((index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const rules = form.getValues('rules');
    if (targetIndex < 0 || targetIndex >= rules.length) return;

    const reordered = [...rules];
    const current = reordered[index];
    const target = reordered[targetIndex];
    if (!current || !target) return;
    reordered[index] = target;
    reordered[targetIndex] = current;
    replace(reprioritizeManipulationRules(reordered));
  }, [form, replace]);

  const onSubmit = async (data: ManipulationFormData) => {
    setSubmitError(null);
    try {
      if (profile) {
        await updateMutation.mutateAsync({
          id: profile.id,
          data: manipulationFormToUpdatePayload(data),
        });
      } else {
        await createMutation.mutateAsync(manipulationFormToCreatePayload(data));
      }
      onSuccess();
      onClose();
      form.reset(buildManipulationDefaultValues(null));
    } catch (error) {
      setSubmitError(describeSubmitError(error, 'Failed to save manipulation profile'));
    }
  };

  const tabs: ResourceEditorTab[] = useMemo(
    () => [
      {
        id: 'profile',
        label: 'Profile',
        icon: Settings,
        fields: ['name', 'description', 'enabled'],
        render: () => <ProfileTab form={form} />,
      },
      {
        id: 'rules',
        label: `Rules (${fields.length})`,
        icon: Wand2,
        fields: ['rules'],
        render: () => (
          <RulesTab
            form={form}
            fields={fields}
            expandedRuleId={expandedRuleId}
            setExpandedRuleId={setExpandedRuleId}
            addRule={addRule}
            removeRule={remove}
            moveRule={moveRule}
          />
        ),
      },
    ],
    [form, fields, expandedRuleId, addRule, moveRule, remove]
  );

  const onInvalid = (errors: FieldErrors<ManipulationFormData>) => {
    const targetTab = firstTabWithError(errors, tabs);
    if (targetTab) setActiveTab(targetTab);
  };

  return (
    <ResourceEditor
      isOpen
      onClose={onClose}
      title={isEditing ? 'Edit Manipulation Profile' : 'Create Manipulation Profile'}
      subtitle="Define rules to transform SIP message headers and URIs"
      headerIcon={(
        <div className="rounded-lg bg-blue-100 p-2">
          <Wand2 className="h-6 w-6 text-blue-600" />
        </div>
      )}
      headerBanner={submitError ? (
        <div className="bg-red-50 px-6 py-3">
          <p className="text-sm text-red-800">{submitError}</p>
        </div>
      ) : undefined}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      errors={form.formState.errors}
      onSubmit={form.handleSubmit(onSubmit, onInvalid)}
      onCancel={onClose}
      isSaving={isSaving}
      saveLabel={isEditing ? 'Save Changes' : 'Create Profile'}
      savingLabel="Saving..."
      footerStart={<span className="text-sm text-gray-500">{fields.length} rule{fields.length === 1 ? '' : 's'} configured</span>}
    />
  );
}

function ProfileTab({ form }: { form: UseFormReturn<ManipulationFormData> }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
          <input
            type="text"
            {...form.register('name')}
            placeholder="e.g., Carrier Normalization"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {form.formState.errors.name && (
            <p className="mt-1 text-sm text-red-600">{form.formState.errors.name.message}</p>
          )}
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
          <label className="mt-2 flex items-center gap-2">
            <input
              type="checkbox"
              {...form.register('enabled')}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Enable this profile</span>
          </label>
        </div>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
        <textarea
          {...form.register('description')}
          placeholder="Optional description of this manipulation profile..."
          rows={3}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );
}

interface RulesTabProps {
  form: UseFormReturn<ManipulationFormData>;
  fields: Array<{ formId: string }>;
  expandedRuleId: string | null;
  setExpandedRuleId: (id: string | null) => void;
  addRule: () => void;
  removeRule: (index: number) => void;
  moveRule: (index: number, direction: 'up' | 'down') => void;
}

function RulesTab({ form, fields, expandedRuleId, setExpandedRuleId, addRule, removeRule, moveRule }: RulesTabProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Manipulation Rules ({fields.length})</h3>
        <button
          type="button"
          onClick={addRule}
          className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-100"
        >
          <Plus className="h-4 w-4" />
          Add Rule
        </button>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
          <div className="text-sm text-blue-800">
            <p className="mb-1 font-medium">How manipulation rules work:</p>
            <ul className="list-inside list-disc space-y-1 text-blue-700">
              <li>Rules are processed in priority order (lowest first)</li>
              <li>Each rule targets a specific header and field</li>
              <li>Patterns support regular expressions with capture groups ($1, $2, etc.)</li>
              <li>Optional conditions can limit when a rule applies</li>
            </ul>
          </div>
        </div>
      </div>

      {fields.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 py-8 text-center">
          <Wand2 className="mx-auto mb-3 h-10 w-10 text-gray-400" />
          <p className="mb-3 text-gray-500">No rules defined yet</p>
          <button
            type="button"
            onClick={addRule}
            className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
          >
            Add First Rule
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {fields.map((field, index) => (
            <RuleEditor
              key={field.formId}
              form={form}
              index={index}
              totalRules={fields.length}
              expanded={expandedRuleId === field.formId}
              onToggleExpand={() => setExpandedRuleId(expandedRuleId === field.formId ? null : field.formId)}
              onMoveUp={() => moveRule(index, 'up')}
              onMoveDown={() => moveRule(index, 'down')}
              onRemove={() => removeRule(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface RuleEditorProps {
  form: UseFormReturn<ManipulationFormData>;
  index: number;
  totalRules: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

function RuleEditor({ form, index, totalRules, expanded, onToggleExpand, onMoveUp, onMoveDown, onRemove }: RuleEditorProps) {
  const rule = useWatch({ control: form.control, name: `rules.${index}` });
  const error = form.formState.errors.rules?.[index];

  if (!rule) return null;

  return (
    <div className={`rounded-lg border bg-gray-50 ${rule.enabled ? 'border-gray-200' : 'border-gray-200 opacity-60'}`}>
      <div className="flex cursor-pointer items-center justify-between p-4" onClick={onToggleExpand}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <button type="button" onClick={(event) => { event.stopPropagation(); onMoveUp(); }} disabled={index === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
              <ChevronUp className="h-4 w-4" />
            </button>
            <button type="button" onClick={(event) => { event.stopPropagation(); onMoveDown(); }} disabled={index === totalRules - 1} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>
          <span className="text-sm font-medium text-gray-500">#{rule.priority}</span>
          <div className="flex items-center gap-2">
            <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">{rule.target}</span>
            <span className="text-gray-400">.</span>
            <span className="text-xs font-medium text-gray-600">{rule.field}</span>
            <span className="rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">{rule.action}</span>
          </div>
          {rule.description && <span className="max-w-xs truncate text-sm text-gray-500">- {rule.description}</span>}
        </div>

        <div className="flex items-center gap-2">
          {!rule.enabled && <span className="text-xs text-gray-500">Disabled</span>}
          {rule.hasCondition && <span className="text-xs text-purple-600">Conditional</span>}
          <button type="button" onClick={(event) => { event.stopPropagation(); onRemove(); }} className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-200 px-4 pb-4 pt-4">
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-4">
            <SelectField form={form} index={index} field="type" label="Rule Type" options={MANIPULATION_TYPE_OPTIONS} />
            <SelectField form={form} index={index} field="target" label="Target Header" options={MANIPULATION_TARGET_OPTIONS} />
            <SelectField form={form} index={index} field="field" label="Field" options={MANIPULATION_FIELD_OPTIONS} />
            <SelectField form={form} index={index} field="action" label="Action" options={MANIPULATION_ACTION_OPTIONS} />
          </div>

          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Pattern (regex){rule.action === 'set' && <span className="font-normal text-gray-400"> - optional for set</span>}</label>
              <input type="text" {...form.register(`rules.${index}.pattern`)} placeholder="e.g., ^\\+1(\\d{10})$" className={`w-full rounded-lg border px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${error?.pattern ? 'border-red-300' : 'border-gray-300'}`} />
              {error?.pattern?.message && <p className="mt-1 text-sm text-red-600">{error.pattern.message}</p>}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Value{rule.action === 'strip' && <span className="font-normal text-gray-400"> - optional for strip</span>}</label>
              <input type="text" {...form.register(`rules.${index}.value`)} placeholder="e.g., $1 or +44$1" className={`w-full rounded-lg border px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${error?.value ? 'border-red-300' : 'border-gray-300'}`} />
              {error?.value?.message && <p className="mt-1 text-sm text-red-600">{error.value.message}</p>}
            </div>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Priority</label>
              <input type="number" min={0} step={1} {...form.register(`rules.${index}.priority`, { valueAsNumber: true })} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
              <input type="text" {...form.register(`rules.${index}.description`)} placeholder="Optional description of what this rule does" className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2">
              <input type="checkbox" {...form.register(`rules.${index}.enabled`)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className="text-sm text-gray-700">Enabled</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                {...form.register(`rules.${index}.hasCondition`, {
                  onChange: (event: ChangeEvent<HTMLInputElement>) => {
                    form.setValue(`rules.${index}.condition`, event.target.checked ? { target: 'From', field: 'user', pattern: '' } : undefined, { shouldDirty: true, shouldValidate: true });
                  },
                })}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Add condition</span>
            </label>
          </div>

          {rule.hasCondition && (
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
              <p className="mb-3 text-sm font-medium text-purple-800">Only apply this rule when:</p>
              <p className="mb-3 text-xs text-purple-700">Condition patterns are regular expressions matched against the selected target field.</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <ConditionSelectField form={form} index={index} field="target" label="Target" />
                <ConditionSelectField form={form} index={index} field="field" label="Field" />
                <div>
                  <label className="mb-1 block text-xs font-medium text-purple-700">Pattern matches</label>
                  <input type="text" {...form.register(`rules.${index}.condition.pattern`)} placeholder="Regex pattern" className={`w-full rounded-lg border px-2 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${error?.condition?.pattern ? 'border-red-300' : 'border-purple-200'}`} />
                  {error?.condition?.pattern?.message && <p className="mt-1 text-xs text-red-600">{error.condition.pattern.message}</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type RuleSelectField = 'type' | 'target' | 'field' | 'action';

function SelectField({ form, index, field, label, options }: {
  form: UseFormReturn<ManipulationFormData>;
  index: number;
  field: RuleSelectField;
  label: string;
  options: ReadonlyArray<{ value: string; label: string }>;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
      <select {...form.register(`rules.${index}.${field}`)} className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </div>
  );
}

type ConditionSelectFieldName = 'target' | 'field';

function ConditionSelectField({ form, index, field, label }: {
  form: UseFormReturn<ManipulationFormData>;
  index: number;
  field: ConditionSelectFieldName;
  label: string;
}) {
  const options = field === 'target' ? MANIPULATION_TARGET_OPTIONS : MANIPULATION_FIELD_OPTIONS;
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-purple-700">{label}</label>
      <select {...form.register(`rules.${index}.condition.${field}`)} className="w-full rounded-lg border border-purple-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500">
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </div>
  );
}

export default ManipulationEditor;
